import { View, Text } from "@react-pdf/renderer";
import dayjs from "dayjs";
import { CoverageRows } from "config";
import {
  getRubricVerdict,
  RATING_LEVELS,
} from "components/dashboard/body/projects/panels/coverage/coverage.constants";
import { getRubricAverage } from "components/dashboard/body/projects/panels/coverage/utils/rubricScore";
import { COVERAGE_RECOMMENDATION_LABELS } from "components/dashboard/body/projects/panels/coverage/utils/coverageRecommendation";
import { RubicIndex } from "components/dashboard/body/projects/panels/coverage/types";
import { CoveragePDFPropsType, DetailRowProps, TextRowProps } from "./CoveragePDF.types";
import {
  PDF_COLORS,
  RECOMMENDATION_CHIP_COLORS,
  RATING_CELL_COLORS,
  styles,
} from "./CoveragePDF.styles";

const TextRow = ({ children, style, hyphenationCallback }: TextRowProps) => (
  <View style={[styles.fullWidth]}>
    <Text style={style} hyphenationCallback={hyphenationCallback}>
      {children || "-"}
    </Text>
  </View>
);

const MainRow = ({ title, value, direction = "row" }: DetailRowProps) => (
  <View style={[styles.tbody, { flexDirection: direction }]}>
    <View style={[styles.thead, { width: direction === "row" ? "18%" : "100%" }]}>
      <TextRow style={styles.theadText} hyphenationCallback={(e) => [e]}>
        {title}
      </TextRow>
    </View>
    <View style={[styles.textPadding, { width: direction === "row" ? "82%" : "100%" }]}>
      <TextRow style={styles.text}>{value}</TextRow>
    </View>
  </View>
);

const DetailRow = ({ title, value }: DetailRowProps) => (
  <View style={[styles.detailContainer]}>
    <View style={[styles.thead, { width: "36%" }]}>
      <TextRow style={styles.theadText} hyphenationCallback={(e) => [e]}>
        {title}
      </TextRow>
    </View>
    <View style={[styles.textPadding, { width: "64%" }]}>
      <TextRow style={styles.text}>{value}</TextRow>
    </View>
  </View>
);

const formatRecommendation = (value?: string | null) => {
  if (!value || !(value in COVERAGE_RECOMMENDATION_LABELS)) {
    return "-";
  }
  return COVERAGE_RECOMMENDATION_LABELS[value as keyof typeof COVERAGE_RECOMMENDATION_LABELS];
};

const RecommendationChip = ({ label, value }: { label: string; value?: string | null }) => {
  const chipColors =
    value && RECOMMENDATION_CHIP_COLORS[value]
      ? RECOMMENDATION_CHIP_COLORS[value]
      : {
          backgroundColor: PDF_COLORS.passBg,
          borderColor: PDF_COLORS.passBorder,
          color: PDF_COLORS.passText,
        };

  return (
    <View style={styles.verdictRow}>
      <Text style={styles.verdictLabel}>{label}</Text>
      <View
        style={[
          styles.chip,
          {
            backgroundColor: chipColors.backgroundColor,
            borderColor: chipColors.borderColor,
          },
        ]}
      >
        <Text style={[styles.chipText, { color: chipColors.color }]}>
          {formatRecommendation(value)}
        </Text>
      </View>
    </View>
  );
};

export const CoveragePDF: CoveragePDFPropsType = ({ coverage }) => {
  const rubricAverage = getRubricAverage(coverage.rubric);
  const verdict = getRubricVerdict(rubricAverage);

  const coverageDetails = [
    [
      { title: "Submitted By", value: coverage.submitted_by },
      {
        title: "Coverage Date",
        value: coverage.coverage_date ? dayjs(coverage.coverage_date).format("MMM D, YYYY") : "-",
      },
    ],
    [
      { title: "Submitted To", value: coverage.submitted_to },
      { title: "Author", value: coverage.author },
    ],
    [
      { title: "Length (scenes)", value: coverage.length },
      { title: "Estimated Duration", value: coverage.estimated_duration },
    ],
    [
      { title: "Location", value: coverage.location },
      { title: "Budget", value: coverage.budget?.toLocaleString() },
    ],
    [
      { title: "Reader", value: coverage.reader },
      { title: "Genre", value: coverage.genre },
    ],
  ];

  const analysisSections = [
    { title: "Synopsis", value: coverage.analysis.summary },
    { title: "Strengths", value: coverage.analysis.strengths },
    { title: "Weaknesses", value: coverage.analysis.weaknesses },
    { title: "Themes", value: coverage.analysis.themes },
    { title: "Notes", value: coverage.analysis.notes },
    { title: "Comments", value: coverage.analysis.comments },
  ];

  const metaParts = [
    coverage.genre ? `Genre: ${coverage.genre}` : null,
    coverage.similar_projects ? `Similar: ${coverage.similar_projects}` : null,
  ].filter(Boolean);

  return (
    <View style={{ flexShrink: 0 }}>
      <View style={styles.heroBlock}>
        <Text style={styles.titleText}>{coverage.title || "-"}</Text>
        <Text style={styles.loglineText}>{coverage.logline || "-"}</Text>
        {metaParts.length > 0 ? (
          <Text style={styles.metaLine}>{metaParts.join("  ·  ")}</Text>
        ) : null}
        <View style={styles.heroFooter}>
          <View style={styles.verdictColumn}>
            <RecommendationChip label="Script" value={coverage.script_recommendation} />
            <RecommendationChip label="Writer" value={coverage.writer_recommendation} />
          </View>
          <View style={styles.scoreColumn}>
            <Text style={styles.scoreValue}>
              {rubricAverage !== null ? rubricAverage.toFixed(1) : "—"}
            </Text>
            <Text style={styles.scoreLabel}>/ 4 average</Text>
            {verdict ? (
              <View
                style={[
                  styles.chip,
                  {
                    backgroundColor: RATING_CELL_COLORS[verdict.value].backgroundColor,
                    borderColor: PDF_COLORS.border,
                    marginTop: 4,
                  },
                ]}
              >
                <Text style={[styles.chipText, { color: RATING_CELL_COLORS[verdict.value].color }]}>
                  {verdict.label}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>

      <View style={styles.sectionMargin} wrap={false}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderText}>Scores</Text>
          <Text style={styles.sectionHeaderMeta}>
            {rubricAverage !== null ? `${rubricAverage.toFixed(1)} / 4` : "—"}
            {verdict ? ` · ${verdict.label}` : ""}
          </Text>
        </View>
        {CoverageRows.map((coverageRow) => {
          const rowName = Object.keys(coverageRow)[0];
          const rowKey = Object.values(coverageRow)[0] as RubicIndex;
          const rowValue = coverage.rubric?.[rowKey];

          return (
            <View key={rowKey} style={styles.rubricRow}>
              <Text style={styles.rubricLabel}>{rowName}</Text>
              {RATING_LEVELS.map((level, index) => {
                const isSelected = rowValue === level.value;
                const selectedColors = RATING_CELL_COLORS[level.value];
                const isLast = index === RATING_LEVELS.length - 1;

                return (
                  <View
                    key={level.value}
                    style={[
                      styles.ratingCell,
                      isLast ? styles.ratingCellLast : {},
                      isSelected
                        ? {
                            backgroundColor: selectedColors.backgroundColor,
                          }
                        : {},
                    ]}
                  >
                    <Text
                      style={[
                        styles.ratingCellText,
                        isSelected
                          ? {
                              color: selectedColors.color,
                              fontWeight: "bold",
                            }
                          : {},
                      ]}
                    >
                      {level.label}
                    </Text>
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>

      {analysisSections.map((section) => (
        <View key={section.title} style={styles.sectionMargin}>
          <MainRow title={section.title} value={section.value} direction="column" />
        </View>
      ))}

      <View style={styles.topMargin}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderText}>Report Details</Text>
        </View>
        {coverageDetails.map((detail, index) => (
          <View key={index} style={styles.coverageRow} wrap={false}>
            <DetailRow title={detail[0].title} value={detail[0].value} />
            <DetailRow title={detail[1].title} value={detail[1].value} />
          </View>
        ))}
        <MainRow title="Similar Projects" value={coverage.similar_projects} />
      </View>
    </View>
  );
};

export default CoveragePDF;
